import LabelWrapper from 'components/Forms/Lib/LabelWrapper/LabelWrapper';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import cs from 'date-fns/locale/cs';
import de from 'date-fns/locale/de';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import hu from 'date-fns/locale/hu';
import it from 'date-fns/locale/it';
import pl from 'date-fns/locale/pl';
import pt from 'date-fns/locale/pt';
import ru from 'date-fns/locale/ru';
import sk from 'date-fns/locale/sk';
import { Locale } from 'date-fns/locale/types';
import zhCN from 'date-fns/locale/zh-CN';
import { ChangeEvent, InputHTMLAttributes, useEffect, useState } from 'react';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import DatePicker from 'react-datepicker';
import { ControllerRenderProps } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';
import { ExtractNativePropsFromDefault } from 'typeHelpers/ExtractNativePropsFromDefault';

const localeMap: { [key: string]: Locale } = {
    cs: cs as any as Locale,
    de: de as any as Locale,
    en: enUS as any as Locale,
    es: es as any as Locale,
    hu: hu as any as Locale,
    it: it as any as Locale,
    pl: pl as any as Locale,
    pt: pt as any as Locale,
    ru: ru as any as Locale,
    sk: sk as any as Locale,
    zh: zhCN as any as Locale,
};

type NativeProps = ExtractNativePropsFromDefault<InputHTMLAttributes<HTMLInputElement>, 'name' | 'id'>;

type DateSelectorProps = NativeProps & {
    minDate?: Date | null;
    maxDate?: Date | null;
    label: string | JSX.Element;
    hasError?: boolean;
    isTouched?: boolean;
    fieldRef?: ControllerRenderProps<any, any>;
    placeholderType?: 'static';
    autocomplete?: 'off' | 'on';
    onChange?: (event: ChangeEvent) => void;
    placeholderText?: string;
    dateFormat?: string | string[];
    filterDate?: (date: Date) => boolean;
};

export const DateSelector = ({
    required,
    id,
    name,
    placeholderType,
    placeholderText,
    fieldRef,
    onChange,
    minDate,
    maxDate,
    label = '',
    autoComplete = 'off',
    dateFormat = ['dd/MM/yyyy', 'dd/MM/yy'],
    filterDate,
    hasError,
}: DateSelectorProps) => {
    const { defaultLocale } = useDomainConfig();
    const [value, setValue] = useState<Date | null>(fieldRef?.value ?? null);
    const [isFocused, setIsFocused] = useState<boolean>(false);

    useEffect(() => {
        if (defaultLocale) {
            registerLocale(defaultLocale, localeMap[defaultLocale]);
            setDefaultLocale(defaultLocale);
        }
    }, [defaultLocale]);

    const handleValueChange = (value: Date | null, event: ChangeEvent<HTMLInputElement>) => {
        const utcDate = value ? new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate())) : null;
        setValue(utcDate);
        if (fieldRef) {
            fieldRef.onChange(utcDate);
        } else if (onChange) {
            onChange(event);
        }
    };

    return (
        <LabelWrapper
            htmlFor={id}
            inputType="text-input"
            isEmpty={!(value !== null || isFocused)}
            label={label}
            placeholderType={placeholderType}
            required={required}
        >
            <DatePicker
                isClearable
                scrollableYearDropdown
                showYearDropdown
                autoComplete={autoComplete}
                dateFormat={dateFormat}
                filterDate={filterDate}
                id={id}
                maxDate={maxDate}
                minDate={minDate}
                name={name}
                placeholderText={isFocused ? placeholderText : ''}
                popperClassName="!z-belowOverlay"
                selected={value}
                wrapperClassName="date-selector-wrapper"
                className={twJoin(
                    'peer box-border h-[55px] w-full rounded-lg border border-solid border-border bg-white px-2 pb-1 pt-5 font-normal text-primary w-appearance-none [-moz-appearance:textfield] focus:border-borderFocus focus:outline focus:outline-[3px] focus:outline-outlineFocus',
                    hasError && 'border-redIntense',
                )}
                onBlur={() => setIsFocused(false)}
                onChange={handleValueChange}
                onFocus={() => setIsFocused(true)}
            />
        </LabelWrapper>
    );
};
